import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  GripVertical,
  Image as ImageIcon,
  List,
  MonitorOff,
  Pencil,
  Plus,
  Radio,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  Trash2,
  TriangleAlert,
  Tv,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type Category, type Channel, type FallbackMode, type LineupEntry, mediaUrl, type PlaylistItem, type SiteFallback, type Source } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ImagePicker } from '../components/ImagePicker';
import { Pager, usePaged } from '../components/Pager';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, Modal, PageHeader, Toggle } from '../components/ui';
import { ImportModal } from './LineupImport';
import { LocalPlaylistEditor } from './LocalPlaylist';
import { StreamBadge, streamVerdict, useStreamCheck } from './StreamCheck';
import { ask } from '../components/Confirm';

export { ImagePicker };

type Tab = 'lineup' | 'channels' | 'categories';

export function LiveTvPage() {
  const [tab, setTab] = useState<Tab>('lineup');
  const site = useSite();
  const { can } = useSession();
  const [fallbackOpen, setFallbackOpen] = useState(false);
  return (
    <>
      <PageHeader
        title="Live TV"
        sub="Manage TV channels, organize them in lineups and categories, and control streaming for this site."
        actions={
          site && can('ADMIN') ? (
            <button className="btn" onClick={() => setFallbackOpen(true)}>
              <MonitorOff size={16} /> When a stream fails
            </button>
          ) : undefined
        }
      />
      <div className="tabs">
        {(['lineup', 'channels', 'categories'] as Tab[]).map((t) => (
          <button key={t} className={`tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t === 'lineup' ? `Lineup${site ? ` - ${site.name}` : ''}` : t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      {tab === 'lineup' && <NeedsSite>{site && <Lineup siteId={site.id} />}</NeedsSite>}
      {tab === 'channels' && <Channels siteId={site?.id ?? null} />}
      {tab === 'categories' && <Categories />}
      {fallbackOpen && site && <SiteFallbackModal siteId={site.id} current={site.settings?.fallback ?? null} onClose={() => setFallbackOpen(false)} />}
    </>
  );
}

const useCategories = () => useQuery({ queryKey: ['categories'], queryFn: () => api.get<{ items: Category[] }>('/categories') });
const useChannels = () => useQuery({ queryKey: ['channels'], queryFn: () => api.get<{ items: Channel[] }>('/channels') });

const PALETTE = ['#7c3aed', '#2563eb', '#10b981', '#f59e0b', '#ec4899', '#a855f7', '#0891b2', '#ef4444'];
/** A category's colour: its own, else one from the palette by name (stable). */
function colorOf(c: { name: string; color?: string | null } | undefined | null) {
  if (!c) return '#64748b';
  if (c.color) return c.color;
  let h = 0;
  for (const ch of c.name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export function CategoryChip({ category }: { category: { name: string; color?: string | null } | null | undefined }) {
  if (!category) return <span className="muted">—</span>;
  const c = colorOf(category);
  return (
    <span className="chip" style={{ color: c, background: `${c}1a` }}>
      {category.name}
    </span>
  );
}

function Logo({ channel, large }: { channel: Pick<Channel, 'name' | 'logo'>; large?: boolean }) {
  return channel.logo ? (
    <span className={`logo-tile${large ? ' large' : ''}`}>
      <img src={mediaUrl(channel.logo.storageKey)} alt="" />
    </span>
  ) : (
    <span className={`logo-tile empty${large ? ' large' : ''}`}>{channel.name.slice(0, 3)}</span>
  );
}

const primaryLink = (c: Channel) => c.categories.find((x) => x.isPrimary) ?? c.categories[0];
const primaryCategory = (c: Channel) => primaryLink(c)?.category ?? null;

const PROTOCOL_LABEL: Record<string, string> = {
  UDP_MULTICAST: 'UDP multicast',
  RTP_MULTICAST: 'RTP multicast',
  HTTP_TS: 'HTTP MPEG-TS',
  HLS: 'HLS',
  DASH: 'DASH',
  RTSP: 'RTSP',
};
const sourceText = (s: Pick<Source, 'protocol' | 'host' | 'port' | 'url'> | undefined) =>
  s ? s.url || (s.host ? `${s.protocol === 'RTP_MULTICAST' ? 'rtp' : 'udp'}://${s.host}:${s.port}` : '') : '';

const FALLBACK_LABEL: Record<FallbackMode, string> = { NO_SIGNAL: '“No Signal” message', IMAGE: 'Picture', VIDEO: 'Looping video' };

function StatCard({ icon, tone, value, label }: { icon: ReactNode; tone: string; value: number | string; label: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-label">{label}</span>
      </span>
    </div>
  );
}

/** "More" button: extra filters and actions in a small menu under it. */
function MoreMenu({ children, active }: { children: ReactNode; active?: boolean }) {
  return (
    <Menu
      minWidth={260}
      button={({ onClick, open }) => (
        <button type="button" className={`btn${active ? ' btn-active' : ''}`} onClick={onClick} aria-expanded={open}>
          <SlidersHorizontal size={16} /> More <ChevronDown size={14} />
        </button>
      )}
    >
      {children}
    </Menu>
  );
}

// ─────────────────────────── Lineup ───────────────────────────

type StatusFilter = 'all' | 'OK' | 'FAIL' | 'none' | 'off';

function Lineup({ siteId }: { siteId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const lineup = useQuery({ queryKey: ['lineup', siteId], queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`) });
  const categories = useCategories();
  const [edit, setEdit] = useState<LineupEntry | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [proto, setProto] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [withFallback, setWithFallback] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['lineup', siteId] });
    void qc.invalidateQueries({ queryKey: ['channels'] });
    void qc.invalidateQueries({ queryKey: ['categories'] });
  };
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: object }) => api.patch(`/lineup/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/lineup/${id}`), onSuccess: invalidate });
  const bulk = useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'on' | 'off' | 'remove' }) => {
      for (const id of ids) {
        if (action === 'remove') await api.delete(`/lineup/${id}`);
        else await api.patch(`/lineup/${id}`, { enabled: action === 'on' });
      }
    },
    onSuccess: () => {
      setSelected(new Set());
      invalidate();
    },
  });
  const check = useStreamCheck('siteChannelIds', () => void qc.invalidateQueries({ queryKey: ['lineup', siteId] }));

  const items = lineup.data?.items ?? [];
  const verdict = (e: LineupEntry) => streamVerdict(e.sources).status;
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((e) => {
      if (s) {
        const hay = `${e.number} ${e.channel.name} ${sourceText(e.sources[0])} ${e.channel.categories.map((c) => c.category.name).join(' ')}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (cat !== 'all' && !e.channel.categories.some((c) => c.categoryId === cat)) return false;
      if (proto === 'LOCAL' && e.kind !== 'LOCAL') return false;
      if (proto !== 'all' && proto !== 'LOCAL' && e.sources[0]?.protocol !== proto) return false;
      if (withFallback && !e.fallbackMode) return false;
      if (status === 'off') return !e.enabled;
      if (status === 'OK' || status === 'FAIL') return verdict(e) === status;
      if (status === 'none') return verdict(e) === null || verdict(e) === 'UNCHECKED';
      return true;
    });
  }, [items, q, cat, proto, status, withFallback]);
  const { rows, pager } = usePaged(shown, 10);
  const counts = { total: items.length, ok: items.filter((e) => verdict(e) === 'OK').length, fail: items.filter((e) => verdict(e) === 'FAIL').length };
  const nextNumber = Math.max(100, ...items.map((i) => i.number)) + 1;
  const pageIds = rows.map((r) => r.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleSel = (id: string) =>
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  if (lineup.isLoading) return <Loading />;
  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<Radio size={20} />} tone="violet" value={counts.total} label="Total channels" />
        <StatCard icon={<Tv size={20} />} tone="green" value={counts.ok} label="Streaming" />
        <StatCard icon={<TriangleAlert size={20} />} tone="red" value={counts.fail} label="Not streaming" />
        <StatCard icon={<List size={20} />} tone="blue" value={categories.data?.items.length ?? 0} label="Categories" />
      </div>

      <div className="card fill">
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search channels (name, number, category…)" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="all">All categories</option>
            {(categories.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select value={proto} onChange={(e) => setProto(e.target.value)} aria-label="Stream type">
            <option value="all">All streams</option>
            <option value="LOCAL">Local channels</option>
            {Object.entries(PROTOCOL_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} aria-label="Status">
            <option value="all">All status</option>
            <option value="OK">Streaming</option>
            <option value="FAIL">Not streaming</option>
            <option value="none">Not checked</option>
            <option value="off">Switched off</option>
          </Select>
          <MoreMenu active={withFallback}>
            <button type="button" className="more-item" onClick={() => setWithFallback((v) => !v)}>
              {withFallback ? <Check size={15} /> : <span style={{ width: 15 }} />} Only channels with their own fallback
            </button>
            {can('OPERATOR') && items.length > 0 && (
              <button type="button" className="more-item" disabled={check.busy} onClick={() => void check.run(shown.map((e) => e.id))}>
                <Activity size={15} /> Check streams ({shown.length})
              </button>
            )}
            {can('ADMIN') && (
              <button type="button" className="more-item" onClick={() => setImporting(true)}>
                <Upload size={15} /> Import CSV / M3U
              </button>
            )}
          </MoreMenu>
          {can('ADMIN') && (
            <button className="btn primary" onClick={() => setEdit('new')}>
              <Plus size={16} /> Add channel
            </button>
          )}
        </div>
        {check.progress && (
          <div className="progress-line">
            Checking streams {check.progress.done} / {check.progress.total}…
          </div>
        )}

        {selected.size > 0 && can('ADMIN') && (
          <div className="bulk-bar">
            <strong>{selected.size} selected</strong>
            <button className="btn small" disabled={check.busy} onClick={() => void check.run([...selected])}>
              <Activity size={14} /> Check
            </button>
            <button className="btn small" onClick={() => bulk.mutate({ ids: [...selected], action: 'on' })}>Switch on</button>
            <button className="btn small" onClick={() => bulk.mutate({ ids: [...selected], action: 'off' })}>Switch off</button>
            <button className="btn small danger" onClick={() => ask(`Remove ${selected.size} channels from this site?`, () => bulk.mutate({ ids: [...selected], action: 'remove' }))}>
              <Trash2 size={14} /> Remove
            </button>
            <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => setSelected(new Set())} title="Clear selection">
              <X size={16} />
            </button>
          </div>
        )}

        {items.length === 0 ? (
          <Empty>No channels in this site's lineup yet. Use “More → Import CSV / M3U” to add many at once.</Empty>
        ) : shown.length === 0 ? (
          <Empty>No channel matches these filters.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {can('ADMIN') && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={allOnPage}
                          onChange={() =>
                            setSelected((cur) => {
                              const n = new Set(cur);
                              pageIds.forEach((id) => (allOnPage ? n.delete(id) : n.add(id)));
                              return n;
                            })
                          }
                          aria-label="Select this page"
                        />
                      </th>
                    )}
                    <th style={{ width: 64 }}>No.</th>
                    <th style={{ width: 64 }}>Logo</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Stream</th>
                    <th>Status</th>
                    <th>Enabled</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} className={e.enabled ? undefined : 'row-off'}>
                      {can('ADMIN') && (
                        <td>
                          <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSel(e.id)} aria-label={`Select ${e.channel.name}`} />
                        </td>
                      )}
                      <td>
                        <strong>{e.number}</strong>
                      </td>
                      <td>
                        <Logo channel={e.channel} />
                      </td>
                      <td>
                        <div className="cell-title">{e.channel.name}</div>
                        {(e.notes || (e.fallbackMode && e.fallbackMode !== 'NO_SIGNAL')) && (
                          <div className="cell-sub">
                            {e.fallbackMode && e.fallbackMode !== 'NO_SIGNAL' && (
                              <span title="Shown when the stream fails">
                                {e.fallbackMode === 'IMAGE' ? <ImageIcon size={12} /> : <Video size={12} />} {FALLBACK_LABEL[e.fallbackMode]}
                              </span>
                            )}
                            {e.notes && <span>{e.notes}</span>}
                          </div>
                        )}
                      </td>
                      <td>
                        <CategoryChip category={primaryCategory(e.channel)} />
                      </td>
                      {e.kind === 'LOCAL' ? (
                        <>
                          <td className="cell-source">
                            {e.playlist.filter((p) => p.type === 'VIDEO').length} videos, {e.playlist.filter((p) => p.type === 'IMAGE').length} pictures
                          </td>
                          <td>
                            <span className="chip" style={{ color: '#7c3aed', background: '#7c3aed14' }}>
                              Local channel
                            </span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="mono cell-source" title={sourceText(e.sources[0])}>
                            {sourceText(e.sources[0])}
                            {e.sources.length > 1 && <span className="badge" style={{ marginLeft: 6 }}>+{e.sources.length - 1}</span>}
                          </td>
                          <td>
                            <span className="chip" style={{ color: '#2563eb', background: '#2563eb14' }}>
                              {PROTOCOL_LABEL[e.sources[0]?.protocol ?? ''] ?? e.sources[0]?.protocol}
                            </span>
                          </td>
                        </>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {e.kind === 'LOCAL' ? <span className="badge brand">Playlist</span> : <StreamBadge sources={e.sources} checking={check.pending.has(e.id)} />}
                        {can('OPERATOR') && e.kind !== 'LOCAL' && (
                          <button className="icon-btn" title="Check this stream" disabled={check.busy} onClick={() => void check.run([e.id])}>
                            <Activity size={14} />
                          </button>
                        )}
                      </td>
                      <td>
                        <Toggle on={e.enabled} onChange={() => can('ADMIN') && patch.mutate({ id: e.id, body: { enabled: !e.enabled } })} label="Enabled" />
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {can('ADMIN') && (
                          <>
                            <button className="icon-btn" onClick={() => setEdit(e)} title="Edit">
                              <Pencil size={16} />
                            </button>
                            <button className="icon-btn" onClick={() => ask(`Remove ${e.channel.name} from this site?`, () => remove.mutate(e.id))} title="Remove">
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager {...pager} />
          </>
        )}
        <ErrorBox error={check.error ?? bulk.error ?? patch.error} />
      </div>
      {edit && <LineupEntryModal siteId={siteId} entry={edit === 'new' ? null : edit} nextNumber={nextNumber} onClose={() => setEdit(null)} onDone={invalidate} />}
      {importing && <ImportModal siteId={siteId} nextNumber={nextNumber} onClose={() => setImporting(false)} onDone={invalidate} />}
    </>
  );
}

const emptySource = (): Source => ({ protocol: 'UDP_MULTICAST', host: '', port: 5000, url: null, priority: 0, enabled: true });

/** Fallback choice shared by a channel (with "site default") and the site default itself. */
function FallbackEditor({
  mode,
  imageUrl,
  videoUrl,
  allowDefault,
  onChange,
}: {
  mode: FallbackMode | null;
  imageUrl: string | null;
  videoUrl: string;
  allowDefault: boolean;
  onChange: (p: { mode?: FallbackMode | null; imageAssetId?: string | null; imageUrl?: string | null; videoUrl?: string }) => void;
}) {
  const movies = useQuery({ queryKey: ['vod', 'items'], queryFn: () => api.get<{ items: { id: string; title: string; sourceUrl: string }[] }>('/vod/titles') });
  const options: { value: FallbackMode | null; icon: ReactNode; label: string; hint: string }[] = [
    ...(allowDefault ? [{ value: null, icon: <ShieldAlert size={18} />, label: 'Site default', hint: 'The choice made for the whole site' }] : []),
    { value: 'NO_SIGNAL', icon: <MonitorOff size={18} />, label: 'No Signal', hint: 'A “No Signal” message over black' },
    { value: 'IMAGE', icon: <ImageIcon size={18} />, label: 'Picture', hint: 'A full-screen picture, e.g. “Back soon”' },
    { value: 'VIDEO', icon: <Video size={18} />, label: 'Looping video', hint: 'A video played again and again' },
  ];
  return (
    <div className="stack">
      <div className="choice-grid">
        {options.map((o) => (
          <button key={String(o.value)} type="button" className={`choice${mode === o.value ? ' on' : ''}`} onClick={() => onChange({ mode: o.value })}>
            <span className="choice-icon">{o.icon}</span>
            <span className="choice-label">{o.label}</span>
            <span className="choice-hint">{o.hint}</span>
            {mode === o.value && <Check size={14} className="choice-check" />}
          </button>
        ))}
      </div>
      {mode === 'IMAGE' && (
        <ImagePicker
          variant="drop"
          label="Picture shown full screen"
          hint="1920×1080 works best."
          value={imageUrl}
          wide
          dark={false}
          height={150}
          onChange={(a) => onChange({ imageAssetId: a?.id ?? null, imageUrl: a?.url ?? null })}
        />
      )}
      {mode === 'VIDEO' && (
        <Field label="Video address" hint="An MP4 or HLS (.m3u8) address the TVs can reach. It plays in a loop until the channel works again.">
          <input placeholder="https://…/promo.mp4" value={videoUrl} onChange={(e) => onChange({ videoUrl: e.target.value })} />
          {(movies.data?.items.length ?? 0) > 0 && (
            <Select value="" onChange={(e) => onChange({ videoUrl: e.target.value })} aria-label="Pick a movie">
              <option value="">…or pick a video from the VOD library</option>
              {movies.data!.items.map((m) => (
                <option key={m.id} value={m.sourceUrl}>
                  {m.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </div>
  );
}

function SiteFallbackModal({ siteId, current, onClose }: { siteId: string; current: SiteFallback | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<FallbackMode>(current?.mode ?? 'NO_SIGNAL');
  const [imageAssetId, setImageAssetId] = useState<string | null>(current?.imageAssetId ?? null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState(current?.videoUrl ?? '');
  const assets = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.get<{ items: { id: string; url: string }[] }>('/assets'),
    enabled: !!current?.imageAssetId,
  });
  const shownImage = imageUrl ?? assets.data?.items.find((a) => a.id === imageAssetId)?.url ?? null;
  const save = useMutation({
    mutationFn: () => api.patch(`/sites/${siteId}`, { fallback: { mode, imageAssetId: mode === 'IMAGE' ? imageAssetId : null, videoUrl: mode === 'VIDEO' ? videoUrl || null : null } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      onClose();
    },
  });
  const incomplete = (mode === 'IMAGE' && !imageAssetId) || (mode === 'VIDEO' && !videoUrl);
  return (
    <Modal
      title="When a stream fails"
      onClose={onClose}
      medium
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={save.isPending || incomplete} onClick={() => save.mutate()}>
            Save changes
          </button>
        </>
      }
    >
      <div className="muted">
        When none of a channel's sources plays, the TV shows this instead. Each channel can choose its own in its settings; this is the default for all the others.
      </div>
      <FallbackEditor
        mode={mode}
        imageUrl={shownImage}
        videoUrl={videoUrl}
        allowDefault={false}
        onChange={(p) => {
          if (p.mode) setMode(p.mode);
          if (p.imageAssetId !== undefined) setImageAssetId(p.imageAssetId);
          if (p.imageUrl !== undefined) setImageUrl(p.imageUrl);
          if (p.videoUrl !== undefined) setVideoUrl(p.videoUrl);
        }}
      />
      <ErrorBox error={save.error} />
    </Modal>
  );
}

/** Add / edit a channel of this site: the channel itself (name, logo, category) and its lineup entry. */
function LineupEntryModal({ siteId, entry, nextNumber, onClose, onDone }: { siteId: string; entry: LineupEntry | null; nextNumber: number; onClose: () => void; onDone: () => void }) {
  const channels = useChannels();
  const categories = useCategories();
  const qc = useQueryClient();
  const ch = entry?.channel;
  const [name, setName] = useState(ch?.name ?? '');
  const [existingId, setExistingId] = useState<string>('');
  const [number, setNumber] = useState(entry?.number ?? nextNumber);
  const [categoryId, setCategoryId] = useState(ch ? primaryLink(ch)?.categoryId ?? '' : '');
  const [logo, setLogo] = useState<{ id: string | null; url: string | null }>({ id: ch?.logoAssetId ?? null, url: ch?.logo ? mediaUrl(ch.logo.storageKey) : null });
  const [artwork, setArtwork] = useState<{ id: string | null; url: string | null }>({ id: ch?.artworkAssetId ?? null, url: ch?.artwork ? mediaUrl(ch.artwork.storageKey) : null });
  const [sources, setSources] = useState<Source[]>(entry?.sources.length ? entry.sources : [emptySource()]);
  const [fbMode, setFbMode] = useState<FallbackMode | null>(entry?.fallbackMode ?? null);
  const [fbImage, setFbImage] = useState<{ id: string | null; url: string | null }>({
    id: entry?.fallbackImageAssetId ?? null,
    url: entry?.fallbackImage ? mediaUrl(entry.fallbackImage.storageKey) : null,
  });
  const [fbVideo, setFbVideo] = useState(entry?.fallbackVideoUrl ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [newCategory, setNewCategory] = useState<string | null>(null);
  const [kind, setKind] = useState<'STREAM' | 'LOCAL'>(entry?.kind ?? 'STREAM');
  const [playlist, setPlaylist] = useState<PlaylistItem[]>(entry?.playlist ?? []);

  const free = (channels.data?.items ?? []).filter((c) => !c.lineups.some((l) => l.siteId === siteId));
  const set = (i: number, p: Partial<Source>) => setSources(sources.map((s, j) => (j === i ? { ...s, ...p } : s)));

  const save = useMutation({
    mutationFn: async () => {
      let catId = categoryId;
      if (newCategory) {
        const key = newCategory.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `cat-${Date.now()}`;
        const c = await api.post<Category>('/categories', { name: newCategory, key });
        catId = c.id;
      }
      const channelBody = { name: name.trim(), logoAssetId: logo.id, artworkAssetId: artwork.id };
      let channelId = entry?.channelId ?? existingId;
      if (channelId) {
        // Keep the channel's other categories; the chosen one becomes the primary one.
        const current = (entry?.channel ?? free.find((c) => c.id === channelId))?.categories.map((c) => c.categoryId) ?? [];
        const ids = catId ? [...new Set([catId, ...current])] : current;
        await api.patch(`/channels/${channelId}`, { ...channelBody, categoryIds: ids, primaryCategoryId: catId || null });
      } else {
        const c = await api.post<Channel>('/channels', { ...channelBody, ...(catId ? { categoryIds: [catId], primaryCategoryId: catId } : {}) });
        channelId = c.id;
      }
      const local = kind === 'LOCAL';
      const body = {
        number,
        kind,
        playlist: local
          ? playlist.map((p) => ({ type: p.type, assetId: p.assetId ?? null, url: p.assetId ? null : p.url ?? null, durationSec: p.type === 'IMAGE' ? p.durationSec ?? 10 : null, title: p.title || null }))
          : [],
        sources: local ? [] : sources.map((s, i) => ({
          protocol: s.protocol,
          host: s.protocol.endsWith('MULTICAST') ? s.host || null : null,
          port: s.protocol.endsWith('MULTICAST') ? s.port || null : null,
          url: s.protocol.endsWith('MULTICAST') ? null : s.url || null,
          priority: i,
          enabled: s.enabled,
        })),
        fallbackMode: fbMode,
        fallbackImageAssetId: fbMode === 'IMAGE' ? fbImage.id : null,
        fallbackVideoUrl: fbMode === 'VIDEO' ? fbVideo || null : null,
        notes: notes.trim() || null,
      };
      return entry ? api.patch(`/lineup/${entry.id}`, body) : api.post(`/sites/${siteId}/lineup`, { ...body, channelId });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      onDone();
      onClose();
    },
  });

  const pickExisting = (id: string) => {
    setExistingId(id);
    const c = free.find((x) => x.id === id);
    if (!c) return;
    setName(c.name);
    setLogo({ id: c.logoAssetId, url: c.logo ? mediaUrl(c.logo.storageKey) : null });
    setArtwork({ id: c.artworkAssetId, url: c.artwork ? mediaUrl(c.artwork.storageKey) : null });
    setCategoryId(primaryLink(c)?.categoryId ?? '');
  };
  const incompleteFallback = kind === 'STREAM' && ((fbMode === 'IMAGE' && !fbImage.id) || (fbMode === 'VIDEO' && !fbVideo));
  const incomplete = incompleteFallback || (kind === 'LOCAL' && playlist.length === 0);

  return (
    <Modal
      title={entry ? 'Edit channel' : 'Add channel'}
      onClose={onClose}
      medium
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name.trim() || save.isPending || incomplete} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : entry ? 'Save changes' : 'Save channel'}
          </button>
        </>
      }
    >
      {entry && (
        <div className="editor-head">
          <span className={`logo-tile large${logo.url ? '' : ' empty'}`}>{logo.url ? <img src={logo.url} alt="" /> : name.slice(0, 3)}</span>
          <div>
            <div className="muted" style={{ fontSize: 12.5 }}>Channel #{entry.number}</div>
            <div className="editor-head-title">{name || entry.channel.name}</div>
          </div>
        </div>
      )}
      {!entry && free.length > 0 && (
        <Field label="Start from a channel you already have (optional)">
          <Select value={existingId} onChange={(e) => pickExisting(e.target.value)}>
            <option value="">New channel</option>
            {free.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="grid cols-2">
        <Field label="Channel name *">
          <input placeholder="e.g. Al Arabiya" value={name} onChange={(e) => setName(e.target.value)} autoFocus={!entry} />
        </Field>
        <Field label="Channel number (this site) *">
          <input type="number" min={1} value={number} onChange={(e) => setNumber(Number(e.target.value))} />
        </Field>
      </div>
      <Field label="Category">
        {newCategory === null ? (
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={{ flex: 1 }}>
              <option value="">Select category</option>
              {(categories.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <button type="button" className="btn" onClick={() => setNewCategory('')}>
              <Plus size={15} /> New
            </button>
          </div>
        ) : (
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <input placeholder="New category name" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} style={{ flex: 1 }} autoFocus />
            <button type="button" className="btn" onClick={() => setNewCategory(null)}>
              Cancel
            </button>
          </div>
        )}
      </Field>
      <div className="grid cols-2">
        <ImagePicker variant="drop" label="Logo" value={logo.url} height={120} onChange={(a) => setLogo({ id: a?.id ?? null, url: a?.url ?? null })} />
        <ImagePicker variant="drop" label="Card artwork (optional)" value={artwork.url} wide dark={false} height={120} onChange={(a) => setArtwork({ id: a?.id ?? null, url: a?.url ?? null })} />
      </div>

      <Field label="Channel type">
        <div className="seg">
          <button type="button" className={`seg-btn${kind === 'STREAM' ? ' on' : ''}`} onClick={() => setKind('STREAM')}>
            <Radio size={15} /> Stream <span className="seg-hint">Multicast, HLS or HTTP from your headend or the internet</span>
          </button>
          <button type="button" className={`seg-btn${kind === 'LOCAL' ? ' on' : ''}`} onClick={() => setKind('LOCAL')}>
            <Video size={15} /> Local channel <span className="seg-hint">Your own videos and pictures, played in a loop</span>
          </button>
        </div>
      </Field>

      {kind === 'LOCAL' ? (
        <>
          <div>
            <h3 style={{ marginBottom: 2 }}>Playlist *</h3>
            <div className="muted" style={{ fontSize: 12.5 }}>Played in this order, then again from the top, with no player controls: guests see it as a normal channel.</div>
          </div>
          <LocalPlaylistEditor items={playlist} onChange={setPlaylist} />
        </>
      ) : (
      <>
      <div>
        <h3 style={{ marginBottom: 2 }}>Stream sources *</h3>
        <div className="muted" style={{ fontSize: 12.5 }}>The TV tries the sources in this order. Start with UDP multicast; other protocols are fallbacks.</div>
      </div>
      {sources.map((s, i) => (
        <div key={i} className="source-row">
          <Select value={s.protocol} onChange={(e) => set(i, { protocol: e.target.value })} style={{ width: 150, flex: 'none' }}>
            {Object.entries(PROTOCOL_LABEL)
              .filter(([k]) => k !== 'DASH' && k !== 'RTSP')
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
          </Select>
          {s.protocol.endsWith('MULTICAST') ? (
            <>
              <input placeholder="239.10.1.101" value={s.host ?? ''} onChange={(e) => set(i, { host: e.target.value })} style={{ flex: 1 }} />
              <input type="number" placeholder="5000" style={{ width: 84 }} value={s.port ?? ''} onChange={(e) => set(i, { port: Number(e.target.value) || null })} />
            </>
          ) : (
            <input placeholder="https://…" style={{ flex: 1 }} value={s.url ?? ''} onChange={(e) => set(i, { url: e.target.value })} />
          )}
          <Toggle on={s.enabled} onChange={(v) => set(i, { enabled: v })} label="Enabled" />
          <span className="muted source-row-state">{s.enabled ? 'Enabled' : 'Off'}</span>
          {sources.length > 1 && (
            <>
              <button className="icon-btn" title="Move up" disabled={i === 0} onClick={() => setSources([...sources.slice(0, i - 1), s, sources[i - 1]!, ...sources.slice(i + 1)])}>
                <ArrowUp size={15} />
              </button>
              <button className="icon-btn" title="Move down" disabled={i === sources.length - 1} onClick={() => setSources([...sources.slice(0, i), sources[i + 1]!, s, ...sources.slice(i + 2)])}>
                <ArrowDown size={15} />
              </button>
            </>
          )}
          <button className="icon-btn" title="Remove" disabled={sources.length === 1} onClick={() => setSources(sources.filter((_, j) => j !== i))}>
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      <div>
        <button className="btn small" onClick={() => setSources([...sources, emptySource()])}>
          <Plus size={14} /> Add source
        </button>
      </div>

      <div className="section-rule" />
      <div>
        <h3 style={{ marginBottom: 2 }}>When the stream doesn't work</h3>
        <div className="muted" style={{ fontSize: 12.5 }}>What the TV shows when none of the sources plays.</div>
      </div>
      <FallbackEditor
        mode={fbMode}
        imageUrl={fbImage.url}
        videoUrl={fbVideo}
        allowDefault
        onChange={(p) => {
          if (p.mode !== undefined) setFbMode(p.mode);
          if (p.imageAssetId !== undefined) setFbImage({ id: p.imageAssetId, url: p.imageUrl ?? null });
          if (p.videoUrl !== undefined) setFbVideo(p.videoUrl);
        }}
      />
      </>
      )}
      <Field label="Notes (optional)">
        <input placeholder="e.g. HD, Arabic, main feed" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <ErrorBox error={save.error} />
    </Modal>
  );
}

// ─────────────────────────── Channels ───────────────────────────

function Channels({ siteId }: { siteId: string | null }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const channels = useChannels();
  const categories = useCategories();
  const lineup = useQuery({
    queryKey: ['lineup', siteId],
    queryFn: () => api.get<{ items: LineupEntry[] }>(`/sites/${siteId}/lineup`),
    enabled: !!siteId,
  });
  const [edit, setEdit] = useState<Channel | 'new' | null>(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/channels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  });
  const check = useStreamCheck('channelIds', () => void qc.invalidateQueries({ queryKey: ['channels'] }));
  const entryOf = useMemo(() => new Map((lineup.data?.items ?? []).map((e) => [e.channelId, e])), [lineup.data]);
  const sourcesOf = (c: Channel) => c.lineups.flatMap((l) => l.sources ?? []);
  const all = channels.data?.items ?? [];
  const shown = all.filter((c) => {
    const s = q.trim().toLowerCase();
    if (s && !`${c.name} ${c.categories.map((x) => x.category.name).join(' ')} ${entryOf.get(c.id)?.number ?? ''}`.toLowerCase().includes(s)) return false;
    if (cat !== 'all' && !c.categories.some((x) => x.categoryId === cat)) return false;
    const v = streamVerdict(sourcesOf(c)).status;
    if (status === 'OK' || status === 'FAIL') return v === status;
    if (status === 'none') return v === null || v === 'UNCHECKED';
    if (status === 'off') return !c.lineups.length;
    return true;
  });
  const { rows, pager } = usePaged(shown, 10);
  const verdicts = all.filter((c) => c.lineups.length).map((c) => streamVerdict(sourcesOf(c)).status);
  if (channels.isLoading) return <Loading />;
  return (
    <>
      <div className="stat-strip">
        <StatCard icon={<Radio size={20} />} tone="violet" value={all.length} label="Total channels" />
        <StatCard icon={<Tv size={20} />} tone="green" value={verdicts.filter((v) => v === 'OK').length} label="Streaming" />
        <StatCard icon={<TriangleAlert size={20} />} tone="red" value={verdicts.filter((v) => v === 'FAIL').length} label="Not streaming" />
        <StatCard icon={<List size={20} />} tone="blue" value={categories.data?.items.length ?? 0} label="Categories" />
      </div>
      <div className="card fill">
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search channels (name, number, category…)" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Category">
            <option value="all">All categories</option>
            {(categories.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} aria-label="Status">
            <option value="all">All status</option>
            <option value="OK">Streaming</option>
            <option value="FAIL">Not streaming</option>
            <option value="none">Not checked</option>
            <option value="off">Not in any lineup</option>
          </Select>
          <MoreMenu>
            {can('OPERATOR') && (
              <button type="button" className="more-item" disabled={check.busy} onClick={() => void check.run(shown.filter((c) => c.lineups.length).map((c) => c.id))}>
                <Activity size={15} /> {check.progress ? `Checking ${check.progress.done} / ${check.progress.total}…` : 'Check streams'}
              </button>
            )}
          </MoreMenu>
          {can('ADMIN') && (
            <button className="btn primary" onClick={() => setEdit('new')}>
              <Plus size={16} /> New channel
            </button>
          )}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 64 }}>No.</th>
                <th style={{ width: 64 }}>Logo</th>
                <th>Name</th>
                <th>Categories</th>
                <th>Stream source</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const e = entryOf.get(c.id);
                return (
                  <tr key={c.id}>
                    <td>{e ? <strong>{e.number}</strong> : <span className="muted">—</span>}</td>
                    <td>
                      <Logo channel={c} />
                    </td>
                    <td className="cell-title">{c.name}</td>
                    <td>
                      <span className="row" style={{ gap: 4 }}>
                        {c.categories.map((x) => (
                          <CategoryChip key={x.categoryId} category={x.category} />
                        ))}
                        {!c.categories.length && <span className="muted">—</span>}
                      </span>
                    </td>
                    <td className="mono cell-source" title={sourceText(e?.sources[0])}>
                      {e ? (
                        <>
                          <span className="muted">{PROTOCOL_LABEL[e.sources[0]?.protocol ?? ''] ?? ''} </span>
                          {sourceText(e.sources[0])}
                        </>
                      ) : (
                        <span className="muted">{c.lineups.length ? `In ${c.lineups.length} other site(s)` : 'Not in a lineup'}</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.lineups.length ? (
                        <>
                          <StreamBadge sources={sourcesOf(c)} checking={check.pending.has(c.id)} />
                          {can('OPERATOR') && (
                            <button className="icon-btn" title="Check this stream" disabled={check.busy} onClick={() => void check.run([c.id])}>
                              <Activity size={14} />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="badge">Not in a lineup</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {can('ADMIN') && (
                        <>
                          <button className="icon-btn" onClick={() => setEdit(c)} title="Edit">
                            <Pencil size={16} />
                          </button>
                          <button className="icon-btn" onClick={() => ask(`Delete ${c.name} everywhere?`, () => remove.mutate(c.id))} title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager {...pager} />
        <ErrorBox error={check.error ?? remove.error} />
      </div>
      {edit && <ChannelModal channel={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function ChannelModal({ channel, onClose }: { channel: Channel | null; onClose: () => void }) {
  const qc = useQueryClient();
  const categories = useCategories();
  const [name, setName] = useState(channel?.name ?? '');
  const [logo, setLogo] = useState<{ id: string | null; url: string | null }>({ id: channel?.logoAssetId ?? null, url: channel?.logo ? mediaUrl(channel.logo.storageKey) : null });
  const [artwork, setArtwork] = useState<{ id: string | null; url: string | null }>({ id: channel?.artworkAssetId ?? null, url: channel?.artwork ? mediaUrl(channel.artwork.storageKey) : null });
  const [categoryIds, setCategoryIds] = useState<string[]>(channel?.categories.map((c) => c.categoryId) ?? []);
  const [primary, setPrimary] = useState<string | null>(channel?.categories.find((c) => c.isPrimary)?.categoryId ?? null);
  const save = useMutation({
    mutationFn: () => {
      const body = { name, logoAssetId: logo.id, artworkAssetId: artwork.id, categoryIds, primaryCategoryId: primary };
      return channel ? api.patch<Channel>(`/channels/${channel.id}`, body) : api.post<Channel>('/channels', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['lineup'] });
      onClose();
    },
  });
  return (
    <Modal
      title={channel ? 'Edit channel' : 'New channel'}
      onClose={onClose}
      medium
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name || save.isPending} onClick={() => save.mutate()}>
            {channel ? 'Save changes' : 'Save channel'}
          </button>
        </>
      }
    >
      {channel && (
        <div className="editor-head">
          <Logo channel={{ name, logo: channel.logo }} large />
          <div>
            <div className="muted" style={{ fontSize: 12.5 }}>Shared by all sites</div>
            <div className="editor-head-title">{name || channel.name}</div>
          </div>
        </div>
      )}
      <Field label="Channel name *">
        <input placeholder="e.g. Al Arabiya" value={name} onChange={(e) => setName(e.target.value)} autoFocus={!channel} />
      </Field>
      <div className="grid cols-2">
        <ImagePicker variant="drop" label="Logo" value={logo.url} height={120} onChange={(a) => setLogo({ id: a?.id ?? null, url: a?.url ?? null })} />
        <ImagePicker variant="drop" label="Card artwork (optional)" value={artwork.url} wide dark={false} height={120} onChange={(a) => setArtwork({ id: a?.id ?? null, url: a?.url ?? null })} />
      </div>
      <Field label="Categories" hint="Click a selected category again to make it the one shown on the TV (★).">
        <div className="row">
          {categories.data?.items.map((c) => {
            const on = categoryIds.includes(c.id);
            const isPrimary = on && (primary ?? categoryIds[0]) === c.id;
            const col = colorOf(c);
            return (
              <button
                key={c.id}
                type="button"
                className="chip chip-btn"
                style={on ? { color: '#fff', background: col } : { color: col, background: `${col}14` }}
                onClick={() => {
                  if (!on) setCategoryIds([...categoryIds, c.id]);
                  else if (!isPrimary) setPrimary(c.id);
                  else {
                    setCategoryIds(categoryIds.filter((x) => x !== c.id));
                    setPrimary(null);
                  }
                }}
              >
                {c.name}
                {isPrimary && ' ★'}
              </button>
            );
          })}
        </div>
      </Field>
      <ErrorBox error={save.error} />
    </Modal>
  );
}

// ─────────────────────────── Categories ───────────────────────────

function Categories() {
  const { can } = useSession();
  const qc = useQueryClient();
  const categories = useCategories();
  const [edit, setEdit] = useState<Category | 'new' | null>(null);
  const [q, setQ] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['categories'] });
    void qc.invalidateQueries({ queryKey: ['channels'] });
  };
  const reorder = useMutation({ mutationFn: (ids: string[]) => api.put('/categories/order', { ids }), onSuccess: invalidate });
  const rename = useMutation({ mutationFn: (c: { id: string; name: string }) => api.patch(`/categories/${c.id}`, { name: c.name }), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/categories/${id}`), onSuccess: invalidate });
  const items = categories.data?.items ?? [];
  const shown = q ? items.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : items;
  const admin = can('ADMIN');
  const drop = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = items.map((c) => c.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId) + (items.findIndex((c) => c.id === dragId) < items.findIndex((c) => c.id === targetId) ? 1 : 0), 0, dragId);
    reorder.mutate(ids);
  };
  if (categories.isLoading) return <Loading />;
  return (
    <>
      <div className="card fill">
        <div className="filters">
          <label className="search">
            <Search size={16} />
            <input placeholder="Search categories…" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {admin && (
            <button className="btn primary" onClick={() => setEdit('new')}>
              <Plus size={16} /> New category
            </button>
          )}
        </div>
        <div className="muted" style={{ fontSize: 13, margin: '0 0 8px' }}>
          Drag the rows to change the order of the category chips on the TV channel list (“All” is always first). The image fills the channel cards of a category
          when a channel has no picture of its own.
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {admin && <th style={{ width: 28 }} />}
                <th style={{ width: 64 }}>Order</th>
                <th style={{ width: 120 }}>Image</th>
                <th>Name</th>
                <th>Channels</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr
                  key={c.id}
                  draggable={admin && !q}
                  onDragStart={() => setDragId(c.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onDragOver={(e) => {
                    if (!dragId) return;
                    e.preventDefault();
                    setOverId(c.id);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    drop(c.id);
                    setDragId(null);
                    setOverId(null);
                  }}
                  className={`${dragId === c.id ? 'row-dragging' : ''}${overId === c.id && dragId !== c.id ? ' row-dropover' : ''}`}
                >
                  {admin && (
                    <td className="drag-handle" title="Drag to reorder">
                      <GripVertical size={16} />
                    </td>
                  )}
                  <td>{items.indexOf(c) + 1}</td>
                  <td>{c.image ? <img className="cat-thumb" src={mediaUrl(c.image.storageKey)} alt="" /> : <span className="cat-thumb empty"><ImageIcon size={18} /></span>}</td>
                  <td>
                    {admin ? (
                      <span className="row" style={{ flexWrap: 'nowrap' }}>
                        <span className="swatch-dot" style={{ background: colorOf(c) }} />
                        <input
                          className="inline-name"
                          defaultValue={c.name}
                          key={c.name}
                          onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && rename.mutate({ id: c.id, name: e.target.value.trim() })}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        />
                      </span>
                    ) : (
                      <CategoryChip category={c} />
                    )}
                  </td>
                  <td className="muted">{c._count?.channels ?? 0} channels</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {admin && (
                      <>
                        <button className="icon-btn" title="Edit" onClick={() => setEdit(c)}>
                          <Pencil size={16} />
                        </button>
                        <button className="icon-btn" title="Delete" onClick={() => ask(`Delete ${c.name}?`, () => remove.mutate(c.id))}>
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ErrorBox error={remove.error ?? reorder.error ?? rename.error} />
      </div>
      {edit && <CategoryModal category={edit === 'new' ? null : edit} all={items} onClose={() => setEdit(null)} />}
    </>
  );
}

function CategoryModal({ category, all, onClose }: { category: Category | null; all: Category[]; onClose: () => void }) {
  const qc = useQueryClient();
  const position = category ? all.findIndex((c) => c.id === category.id) + 1 : all.length + 1;
  const [name, setName] = useState(category?.name ?? '');
  const [order, setOrder] = useState(position);
  const [color, setColor] = useState<string>(category?.color ?? colorOf(category ?? { name: 'new' }));
  const [image, setImage] = useState<{ id: string | null; url: string | null }>({
    id: category?.imageAssetId ?? null,
    url: category?.image ? mediaUrl(category.image.storageKey) : null,
  });
  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), color, imageAssetId: image.id };
      let id = category?.id;
      if (category) await api.patch(`/categories/${category.id}`, body);
      else {
        const key = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `cat-${Date.now()}`;
        id = (await api.post<Category>('/categories', { ...body, key })).id;
      }
      // Move to the chosen position.
      const target = Math.max(1, Math.min(order, all.length + (category ? 0 : 1)));
      if (id && target !== position) {
        const ids = all.map((c) => c.id).filter((x) => x !== id);
        ids.splice(target - 1, 0, id);
        await api.put('/categories/order', { ids });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['categories'] });
      void qc.invalidateQueries({ queryKey: ['channels'] });
      onClose();
    },
  });
  return (
    <Modal
      title={category ? 'Edit category' : 'Add category'}
      onClose={onClose}
      medium
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {category ? 'Save changes' : 'Save category'}
          </button>
        </>
      }
    >
      {category && (
        <div className="editor-head">
          {image.url ? <img className="cat-thumb" src={image.url} alt="" /> : <span className="cat-thumb empty"><ImageIcon size={18} /></span>}
          <div className="editor-head-title">{name || category.name}</div>
        </div>
      )}
      <div className="grid" style={{ gridTemplateColumns: '1fr 120px' }}>
        <Field label="Category name *">
          <input placeholder="e.g. News" value={name} onChange={(e) => setName(e.target.value)} autoFocus={!category} />
        </Field>
        <Field label="Order">
          <input type="number" min={1} max={all.length + 1} value={order} onChange={(e) => setOrder(Number(e.target.value) || 1)} />
        </Field>
      </div>
      <div className="grid cols-2">
        <ImagePicker variant="drop" label="Image" hint="Fills the channel cards when a channel has no picture (16:9)." value={image.url} wide dark={false} height={140} onChange={(a) => setImage({ id: a?.id ?? null, url: a?.url ?? null })} />
        <Field label="Preview">
          <div className="cat-preview">{image.url ? <img src={image.url} alt="" /> : <span className="cat-preview-empty"><ImageIcon size={28} /></span>}</div>
          <div style={{ textAlign: 'center', marginTop: 6 }}>
            <CategoryChip category={{ name: name || 'Category', color }} />
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{category?._count?.channels ?? 0} channels</div>
          </div>
        </Field>
      </div>
      <Field label="Colour">
        <div className="row">
          {PALETTE.map((p) => (
            <button key={p} type="button" className={`swatch-btn${color === p ? ' on' : ''}`} style={{ background: p }} onClick={() => setColor(p)} aria-label={p}>
              {color === p && <Check size={14} color="#fff" />}
            </button>
          ))}
        </div>
      </Field>
      <ErrorBox error={save.error} />
    </Modal>
  );
}
